import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MuteStationRequest } from '@monitor/alerting/data/alerting.models';
import { TranslateService } from '@ngx-translate/core';
import { LocationTypes } from '@shared/pipes/location.pipe';
import { DateConversionService } from '@shared/services/util/date-conversion.service';
import { DropdownItem } from '@widgets/dropdown/dropdown-type';
import { ButtonColor } from '@widgets/eop-button/eop-button.component';
import { IconColor, IconSize } from '@widgets/eop-icon/eop-icon.component';
import { MODAL_DATA } from '@widgets/modal/modal.service';
import { ModalRef } from '@widgets/modal/modalRef';

export enum DurationOption {
  EIGHT_HOURS = 'EIGHT_HOURS',
  TWENTY_FOUR_HOURS = 'TWENTY_FOUR_HOURS',
  FOURTH_EIGHT_HOURS = 'FOURTH_EIGHT_HOURS',
  ONE_WEEK = 'ONE_WEEK',
  TWO_WEEKS = 'TWO_WEEKS',
  INDEFINITELY = 'INDEFINITELY',
}

export interface MuteStationModalConfig {
  stationName: string;
  country: string;
  zipCode: string;
  city: string;
  street: string;
}

interface MuteStationForm {
  mutedUntil: FormControl<DropdownItem>;
  userNote: FormControl<string>;
}

@Component({
  selector: 'eop-mute-station-alerts-modal',
  templateUrl: './mute-station-alerts-modal.component.html',
  styleUrl: './mute-station-alerts-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MuteStationAlertsModalComponent implements OnInit {
  form: FormGroup<MuteStationForm>;
  durations: DropdownItem[] = [];
  modalData = inject<MuteStationModalConfig>(MODAL_DATA);

  protected readonly IconColor = IconColor;
  protected readonly ButtonColor = ButtonColor;
  protected readonly LocationTypes = LocationTypes;
  protected readonly IconSize = IconSize;

  private readonly dateConversionService = inject(DateConversionService);
  private readonly translateService = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly modalRef: ModalRef = inject(ModalRef);

  ngOnInit() {
    const defaultDuration = {
      name: '8 ' + this.translateService.instant('GLOBAL.HOURS').toLowerCase(),
      value: DurationOption.EIGHT_HOURS,
    } as DropdownItem;
    this.form = this.fb.group({
      mutedUntil: [defaultDuration],
      userNote: ['', [Validators.maxLength(256)]],
    });
    this.durations = [
      defaultDuration,
      {
        name: '24 ' + this.translateService.instant('GLOBAL.HOURS').toLowerCase(),
        value: DurationOption.TWENTY_FOUR_HOURS,
      } as DropdownItem,
      {
        name: '48 ' + this.translateService.instant('GLOBAL.HOURS').toLowerCase(),
        value: DurationOption.FOURTH_EIGHT_HOURS,
      } as DropdownItem,
      {
        name: '1 ' + this.translateService.instant('GLOBAL.WEEK').toLowerCase(),
        value: DurationOption.ONE_WEEK,
      } as DropdownItem,
      {
        name: '2 ' + this.translateService.instant('GLOBAL.WEEKS').toLowerCase(),
        value: DurationOption.TWO_WEEKS,
      } as DropdownItem,
      {
        name: this.translateService.instant('ALERTING.MUTE_STATION_UNTIL'),
        value: DurationOption.INDEFINITELY,
      } as DropdownItem,
    ];
  }

  onClose() {
    this.modalRef.close(false);
  }

  onMute() {
    const muteConfig: MuteStationRequest = {
      mutedUntil: this.calculateMutedUntil(
        this.form.controls.mutedUntil.value.value as DurationOption
      ),
      userNote: this.form.controls.userNote.value,
    };

    this.modalRef.close(muteConfig);
  }

  private calculateMutedUntil(duration: DurationOption): string {
    const now = this.dateConversionService.createMomentInUsersTimezone();
    let mutedUntil: string;

    switch (duration) {
      case DurationOption.EIGHT_HOURS:
        mutedUntil = now.add(8, 'hours').toISOString();
        break;
      case DurationOption.TWENTY_FOUR_HOURS:
        mutedUntil = now.add(24, 'hours').toISOString();
        break;
      case DurationOption.FOURTH_EIGHT_HOURS:
        mutedUntil = now.add(48, 'hours').toISOString();
        break;
      case DurationOption.ONE_WEEK:
        mutedUntil = now.add(1, 'week').toISOString();
        break;
      case DurationOption.TWO_WEEKS:
        mutedUntil = now.add(2, 'weeks').toISOString();
        break;
      case DurationOption.INDEFINITELY:
        mutedUntil = null;
        break;
    }
    return mutedUntil;
  }
}
